import { Link } from 'wouter'

import { useAuth } from './contexts/auth-context'

/**
 * §9.143 — an order number that takes you back to the order.
 *
 * The owner's point 36 asks for this *"všade kde niečo také je"*, and the rule for where that is
 * lives in §9.143: a log, a ledger, an archive or a record links; a live operational board does not.
 * What lives here is the part every one of those call sites has to get identically right.
 *
 * **The id, not the number.** §10A.73: the archive's free-text search matches `%term%` across the
 * customer's phone as well as the order number, so `search=10723` answers four orders belonging to
 * two different people. The link names the row.
 *
 * **A link only if it can be followed.** The archive is behind the `lists` permission. A cook reading
 * a kasa log sees the number either way; what they do not get is an underline that leads to a refusal
 * page. Deciding this once is the reason this is a component rather than a helper each screen calls.
 */

/** The query parameter the archive reads. One spelling, used by both sides. */
export const ARCHIVE_ORDER_PARAM = 'order'

/** Where an order lives in the archive. Exported for the specs and for anything building a URL. */
export function archiveHref(orderId: number): string {
  return `/lists?${ARCHIVE_ORDER_PARAM}=${orderId}`
}

export interface OrderNumberLinkProps {
  /** The row's own id. Absent — an unsaved or synthetic row — renders text, because there is no target. */
  orderId?: number | null
  /** What to show. The number as the screen already writes it, `#` prefix and all. */
  children: React.ReactNode
  /** The call site's own styling, so a link looks like the text it replaced plus an affordance. */
  className?: string
  /** Screen-reader label; call sites pass the number so "odkaz" alone is never the whole name. */
  title?: string
}

export function OrderNumberLink({ orderId, children, className, title }: OrderNumberLinkProps) {
  const { hasPermission } = useAuth()

  // An id of 0 is the fallback several screens use for "unknown", and it is not an order.
  const linkable = typeof orderId === 'number' && orderId > 0 && hasPermission('lists')
  if (!linkable) {
    return <span className={className}>{children}</span>
  }

  return (
    <Link
      href={archiveHref(orderId)}
      className={className}
      data-order-link={orderId}
      title={title}
    >
      {children}
    </Link>
  )
}
